import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { graphBuilderApi } from '../../../services/api';
import '../ProcessListPage.css';
import './ChatAssistantPage.css';

function ChatAssistantListPage() {
  const navigate = useNavigate();

  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const fetchAssistants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await graphBuilderApi.listChatAssistants({ status: statusFilter, limit: 1000 });
      setAssistants(response.assistants || []);
    } catch (err) {
      console.error('Error fetching chat assistants:', err);
      setError(err.message || 'Failed to load chat assistants');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  const handleDelete = async (uuid) => {
    try {
      await graphBuilderApi.archiveChatAssistant(uuid);
      setDeleteConfirm(null);
      fetchAssistants();
    } catch (err) {
      console.error('Error deleting chat assistant:', err);
      setError(err.message || 'Failed to delete chat assistant');
    }
  };

  const handleEdit = (uuid) => {
    navigate(`/user-home/chat-assistant/${uuid}`);
  };

  const handleCopyUrl = async (uuid) => {
    const url = `${window.location.origin}/chatassist/${uuid}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (_) {
      // Fallback for non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopiedId(uuid);
    setTimeout(() => setCopiedId((cur) => (cur === uuid ? null : cur)), 2000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (a, field) => {
    switch (field) {
      case 'name': return (a.name || '').toLowerCase();
      case 'status': return (a.status || '').toLowerCase();
      case 'created_at': return a.created_at ? new Date(a.created_at).getTime() : 0;
      case 'updated_at': return a.updated_at ? new Date(a.updated_at).getTime() : 0;
      default: return '';
    }
  };

  const filtered = assistants.filter((a) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (a.name || '').toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const aVal = getSortValue(a, sortField);
    const bVal = getSortValue(b, sortField);
    let comparison = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="process-list-page">
      <div className="process-list-container">
        <div className="process-list-header">
          <div className="header-left">
            <h1>Chat Assistant</h1>
            <p className="subtitle">
              Published Test Graph snapshots you can embed in external apps at /chatassist/[id]
            </p>
          </div>
        </div>

        <div className="process-list-toolbar">
          <form className="search-form" onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </form>

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <span>Loading chat assistants...</span>
          </div>
        )}

        {!loading && assistants.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <h3>No Chat Assistants Yet</h3>
            <p>Open a graph's Test Graph page, configure it, and click "Publish Chat Agent" to create one.</p>
          </div>
        )}

        {!loading && assistants.length > 0 && (
          <table className="process-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
                <th>Graph</th>
                <th className="sortable" onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                <th className="sortable" onClick={() => handleSort('created_at')}>Created{sortIndicator('created_at')}</th>
                <th className="sortable" onClick={() => handleSort('updated_at')}>Modified{sortIndicator('updated_at')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((a) => (
                <tr key={a.chat_assistant_id} onClick={() => handleEdit(a.chat_assistant_id)}>
                  <td className="process-name">
                    <span className="name-text">{a.name}</span>
                    {a.description && (
                      <span className="name-description">
                        {a.description.length > 100 ? `${a.description.substring(0, 100)}...` : a.description}
                      </span>
                    )}
                  </td>
                  <td className="process-graph">
                    {a.graph_id ? (
                      <span className="graph-badge">{a.graph_id.substring(0, 8)}...</span>
                    ) : (
                      <span className="global-badge">—</span>
                    )}
                  </td>
                  <td className="process-status">
                    <span className={`status-badge ${a.status}`}>{a.status}</span>
                  </td>
                  <td className="process-created">{formatDate(a.created_at)}</td>
                  <td className="process-created">{formatDate(a.updated_at)}</td>
                  <td className="process-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="action-btn copy-url"
                      onClick={() => handleCopyUrl(a.chat_assistant_id)}
                      title="Copy public chat URL"
                    >
                      {copiedId === a.chat_assistant_id ? '✅ Copied' : '🔗 Copy URL'}
                    </button>
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(a.chat_assistant_id)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => setDeleteConfirm(a)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {deleteConfirm && (
          <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Chat Assistant</h3>
              <p>Are you sure you want to delete "{deleteConfirm.name}"?</p>
              <p className="warning">This archives the assistant and disables its public URL. It can be restored later.</p>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                <button className="btn-danger" onClick={() => handleDelete(deleteConfirm.chat_assistant_id)}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatAssistantListPage;
